import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { withLegacyDeps } from '@bingads-webui-campaign-react/legacy-deps-context';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { getEntityFromScope } from '@bingads-webui/edm-campaign';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withGlobalFilter } from '@bingads-webui-react/global-filter-context';
import { WaterfallFilterType, waterfallDateFilterProcessor } from '@bingads-webui-campaign/filter-storage';
import { updateGridQuery } from '@bingads-webui-campaign/summary-grid-helper';
import { buildMergedI18n } from '@bingads-webui-campaign-react/portfolio-bid-strategy-shared';
import { i18nMap } from './i18n-map/i18n-map';
/* eslint-disable */
import globalConfig from '@bingads-webui-component-legacy/config/index';
import * as columnChooserPreferences from '@bingads-webui-component-legacy/bingads-grid/service/column-chooser-preferences';
import { getFilters as preferenceStoreFactory } from '@bingads-webui-component-legacy/bingads-grid/service/filter-preferences';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import edm from '@bingads-webui-component-legacy/edm-campaign/index';
// import { DownloadView } from '@bingads-webui-campaign-legacy/griddownload/view/adapter';
import appState from '@bingads-webui-component-legacy/state/index';
import { constructSchemaI18n } from '@bingads-webui-component-legacy/schema-i18n/loader';
import { fetchPaginationConfig } from '@bingads-webui-component-legacy/bingads-grid/service/fetch-pagination';
import { handleSortChange } from '@bingads-webui-campaign-legacy/bingadsgrid/service/state-manager-helper';
/* eslint-enable */
import { getGridPreferences } from './fetch-preferences';
import { getToolbarConfig } from './toolbar-config';
import { toolbarActionFactory } from './toolbar-action';
import { clearSelection } from './selection-helper';
import { SeasonalityAdjustmentGridView } from './grid/grid-view';
import { SeasonalityAdjustmentGridConfig } from './grid/grid-config';
import { getStyles } from './styles';
import { getFilterConfig } from './filter-config';
import { inlineEditFactory } from './inline-edit';
import { StyledDescriptionBanner } from './description';
import { AdjustmentType, isDataExclusion } from './adjustment-type';

const gridName = 'SeasonalityAdjustmentGrid';

class SeasonalityAdjustmentPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.renderFilterViewManager = _.once(this.renderFilterViewManager);
    this.renderGrid = _.once(this.renderGrid);
    this.renderLazyGrid = _.once(this.renderLazyGrid);

    this.p$componentMount = new Promise((resolve) => {
      this.setComponentMounted = resolve;
    });

    this.p$preferenceConfig = getGridPreferences({
      gridName,
      fetchPaginationConfig,
    });
  }

  componentDidMount() {
    this.setComponentMounted();
  }

  getRawDateFilter = () => this.props[WaterfallFilterType.DateFilter];

  getCookedDateFilter = () => {
    const { serverContext } = this.props;
    const rawDateFilter = this.getRawDateFilter();
    const cookedDateFilter = waterfallDateFilterProcessor([{ filter: rawDateFilter }], serverContext);

    return cookedDateFilter;
  };

  getGridView = () =>
    putil.get(this.backboneGridView, 'grid.gridView');

  clearSelection = () => clearSelection(this.getGridView());

  reloadGrid = ({
    resetSelection = false,
  } = {}) => {
    const gridView = this.getGridView();
    if (gridView) {
      if (resetSelection) {
        this.clearSelection();
      }
      gridView.refresh(true);
    }
  };

  renderFilterViewManager() {
    const {
      i18n,
      preferenceService,
      toolbarSelectedConfig,
      toolbarUnselectedConfig,
      updateToolbar,
      resetToolbarVisibility,
      permissions,
      adjustmentType,
    } = this.props;

    const { getGridView } = this;

    const legacyStringMap = isDataExclusion(adjustmentType) ?
      { ...i18nMap.DataExclusionSchema } :
      { ...i18nMap.SeasonalitySchema };

    const mergedI18n = buildMergedI18n({ i18n, legacyStringMap });

    const filterConfig = getFilterConfig({
      i18n: mergedI18n,
      globalConfig,
      preferenceService,
      preferenceStoreFactory,
      getGridView,
      adjustmentType,
    });

    this.filterService = filterConfig.filterService;

    const toolbarEditor = getToolbarEditor({
      toolbarConfig: { toolbarSelectedConfig, toolbarUnselectedConfig },
      updateToolbar,
    });

    return (
      <FilterViewManagerWithGlobalFilterContext
        updateToolbar={toolbarEditor.update}
        resetToolbarVisibility={resetToolbarVisibility}
        permissions={permissions}
        {...filterConfig}
      />);
  }

  renderLazyGrid() {
    const LazyGrid = React.lazy(() =>
      Promise.all([
        this.p$preferenceConfig,
        this.p$componentMount,
      ]).then(([preferenceConfig]) => ({
        default: () => this.renderGrid(preferenceConfig),
      })));

    return (<LazyGrid />);
  }

  renderGrid(preferenceConfig) {
    const {
      i18n,
      scope,
      permissions,
      onGridInjected,
      toolbarPlugin,
      crossPageSelectPlugin,
      preferenceService,
      perfMarker,
      mockData,
      showTopOfGridPanel,
      adjustmentType,
    } = this.props;
    const { currentAccount } = getEntityFromScope(scope);
    const { getGridView, reloadGrid } = this;

    let dataSource;
    if (mockData) {
      dataSource = mockData;
    } else {
      dataSource = {
        type: 'jsdata',
        entity: jsdata.SeasonalityAdjustment,
        options: {
          entitySet: edm.currentAccount.SeasonalityAdjustments['Default.GridData'].$call({
            gridDataParameters: {},
            adjustmentType,
          }),
          usePendingFindAll: false,
        },
      };
    }

    const legacyStringMap = {
      ...i18nMap.InlineEdit,
      ...i18nMap.columnChooser,
    };

    const mergedI18n = buildMergedI18n({ i18n, legacyStringMap });
    const inlineEditConfig = inlineEditFactory({
      i18n: mergedI18n,
      jsdata,
      currentAccount,
      getGridView,
      reloadGrid,
      adjustmentType,
    });

    const config = new SeasonalityAdjustmentGridConfig({
      adjustmentType,
      gridName,
      i18n: mergedI18n,
      dataSource,
      columnChooserPreferences,
      inlineEditConfig,
      scope,
      permissions,
      preferenceService,
      // DownloadView,
      appState,
      filterService: this.filterService,
      // s$i18n,
      perfMarker,
      globalConfig,
    });

    const finalConfig = putil.merge(config, preferenceConfig);

    const backboneGridView = new SeasonalityAdjustmentGridView({
      config: finalConfig,
      permissions,
      toolbarPlugin,
      crossPageSelectPlugin,
    });

    _.extend(this, { backboneGridView });

    updateGridQuery(getGridView(), this.filterService.getCombinedFilter(), true);

    // Once sort changes, save to preference
    getGridView().on('willUpdate', (changes) => {
      handleSortChange(changes, getGridView(), gridName);
    });

    const toolbarAction = toolbarActionFactory({
      i18n,
      jsdata,
      scope,
      getGridView,
      reloadGrid,
      showTopOfGridPanel,
      adjustmentType,
    });

    onGridInjected({
      backboneGridView,
      reloadGrid,
      toolbarAction,
      deselectAll: this.clearSelection,
    });

    return (
      <StyledGridViewWrapper gridView={backboneGridView} />
    );
  }

  render() {
    const {
      classes,
      forceResetScrollFlag,
      showTopGridPanel,
      panelView,
      renderToolbarContainer,
      adjustmentType,
    } = this.props;

    const topGridPanel = showTopGridPanel ?
      (<TopGridPanel
        panelView={panelView}
        customClasses={{
          topGridPanelCustomStyles: this.props.classes.topGridPanelCustomStyles,
        }}
      />) : null;

    const description = showTopGridPanel ? null : (<StyledDescriptionBanner adjustmentType={adjustmentType} />);

    const topOfGridContent = (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <Fragment>
          {topGridPanel}
          {description}
        </Fragment>
      </React.Suspense>
    );

    const stickyTopOfGridContent = (
      <Fragment>
        {renderToolbarContainer({ isMultiPage: false })}
        {this.renderFilterViewManager()}
      </Fragment>
    );

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <StyledTablePageContainer
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.renderLazyGrid()}
          backboneGridView={this.backboneGridView}
          customClasses={{
            tablePageContainer: classNames('seasonality-adjustment-page', classes.seasonalityTablePageContainer),
            gridViewContainer: classes.seasonalityGridViewContainer,
          }}
          scrollToTop={false}
          forceResetScrollFlag={forceResetScrollFlag}
          disableFooter={false}
          shouldIncludeInlineEditPopup
        />
      </React.Suspense>
    );
  }
}

SeasonalityAdjustmentPage.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    Token: PropTypes.string.isRequired,
  }).isRequired,
  legacyDeps: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  adjustmentType: PropTypes.oneOf([AdjustmentType.DataExclusion, AdjustmentType.SeasonalityAdjustment]).isRequired,
  // Begin: injected by withTablePageStateManager
  toolbarSelectedConfig: PropTypes.shape({}).isRequired,
  toolbarUnselectedConfig: PropTypes.shape({}).isRequired,
  updateToolbar: PropTypes.func.isRequired,
  resetToolbarVisibility: PropTypes.func.isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  onGridInjected: PropTypes.func.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  panelView: PropTypes.shape({}),
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  forceResetScrollFlag: PropTypes.bool.isRequired,
  mockData: PropTypes.shape({}),
  showTopOfGridPanel: PropTypes.func.isRequired,
  // End: injected by withTablePageStateManager
};

SeasonalityAdjustmentPage.defaultProps = {
  panelView: null,
  mockData: null,
};

const Wrapped = _.compose(
  withI18nConsumer,
  withLegacyDeps,
  withPreference,
  withScope,
  withPermissions,
  withServerContext,
  withGlobalFilter,
  Comp => withDefaultStyles(Comp, getStyles),
  withTablePageStateManager({ getToolbarConfig })
)(SeasonalityAdjustmentPage);

Wrapped.defaultProps = {
  adjustmentType: AdjustmentType.SeasonalityAdjustment, // so that getToolbarConfig could get default value
};

export { Wrapped as StyledSeasonalityAdjustmentPage };
